import 'dayjs/locale/pt-br'

import { ptBR } from 'date-fns/locale'
import dayjs from 'dayjs'
import { Calendar as CalendarIcon } from 'lucide-react'
import * as React from 'react'
import type { SelectSingleEventHandler } from 'react-day-picker'

import { cn } from '../../lib/utils'
import { Button } from './button'
import { Calendar } from './calendar'
import { Input } from './input'
import { Label } from './label'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

interface DateTimePickerProps {
  date: Date | undefined
  setDate: (date: Date) => void
}

export function DateTimePicker({ date, setDate }: DateTimePickerProps) {
  const [selectedDateTime, setSelectedDateTime] = React.useState<
    Date | undefined
  >(date)

  const handleSelect: SelectSingleEventHandler = (day, selected) => {
    if (!selected) return

    if (selectedDateTime) {
      selected.setHours(selectedDateTime?.getHours())
      selected.setMinutes(selectedDateTime?.getMinutes())
    }

    setSelectedDateTime(selected)
    setDate(selected)
  }

  type TimeChangeProps = React.ChangeEvent<HTMLInputElement>

  const handleTimeChange = (event: TimeChangeProps) => {
    const value = event.currentTarget.value
    const [hours, minutes] = value.split(':').map(Number)

    let updatedDate = new Date()

    if (selectedDateTime) {
      updatedDate = new Date(selectedDateTime)
    }

    updatedDate.setHours(hours, minutes, 0, 0)

    setSelectedDateTime(updatedDate)
    setDate(updatedDate)
  }

  const footer = (
    <>
      <div className="px-4 pt-0 pb-4">
        <Label>Hora</Label>
        <Input
          type="time"
          onChange={handleTimeChange}
          value={
            selectedDateTime ? dayjs(selectedDateTime).format('HH:mm') : ''
          }
        />
      </div>
      {!selectedDateTime && <p>Selecione uma data e hora</p>}
    </>
  )

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={'outline'}
          className={cn(
            'w-full pl-3 text-left font-normal',
            !selectedDateTime && 'text-muted-foreground',
          )}
        >
          {selectedDateTime ? (
            dayjs(selectedDateTime, 'DD/MM/YYYY HH:mm')
              .locale('pt-br')
              .format('DD [de] MMMM [de] YYYY, HH:mm')
          ) : (
            <span>Selecione uma data</span>
          )}
          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          locale={ptBR}
          mode="single"
          selected={selectedDateTime}
          onSelect={handleSelect}
          disabled={(date) => date < new Date('1900-01-01')}
          initialFocus
        />
        {footer}
      </PopoverContent>
    </Popover>
  )
}
